import { useState } from 'react';
import { Upload, Loader2, AlertCircle, CheckCircle2, FileText, Package } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

export default function AdminShopifyImport() {
  const [file, setFile] = useState(null);
  const [defaultCategory, setDefaultCategory] = useState('À classer');
  const [priceMultiplier, setPriceMultiplier] = useState(1);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Le fichier doit être un .csv');
      return;
    }
    setFile(f);
    setPreview(null);
    setImportResult(null);
  };

  const handlePreview = async () => {
    if (!file) return toast.error('Sélectionnez un fichier CSV');
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('default_category', defaultCategory);
      fd.append('price_multiplier', String(priceMultiplier));
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/shopify/preview-csv`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPreview(data);
      toast.success(`Prévisualisation : ${data.stats?.total_products || 0} produits`);
    } catch (e) {
      console.error('Preview error:', e);
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!file) return toast.error('Sélectionnez un fichier CSV');
    if (!window.confirm(`Confirmer l'import de ${preview?.stats?.total_products || '?'} produits ?`)) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('default_category', defaultCategory);
      fd.append('price_multiplier', String(priceMultiplier));
      fd.append('skip_duplicates', String(skipDuplicates));
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/shopify/import-csv`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setImportResult(data);
      toast.success(`Import terminé : ${data.inserted} importés, ${data.skipped} ignorés`);
    } catch (e) {
      console.error('Import error:', e);
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-shopify-import">
      <div>
        <h2 className="font-serif text-2xl mb-2">Importer depuis Shopify</h2>
        <p className="text-sm text-[#6B6A68]">Exportez vos produits depuis Shopify (Admin → Products → Export → CSV) puis chargez le fichier ici.</p>
      </div>

      <div className="bg-white border border-[#E0DFDD] p-6 rounded space-y-4">
        <div>
          <label className="text-sm font-medium block mb-2">Fichier CSV Shopify</label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 px-4 py-2 border border-[#E0DFDD] cursor-pointer hover:bg-[#F9F8F6] text-sm">
              <Upload size={16} />
              {file ? file.name : 'Sélectionner un fichier...'}
              <input type="file" accept=".csv" onChange={handleFileChange} className="hidden" data-testid="shopify-file-input" />
            </label>
            {file && <span className="text-xs text-[#6B6A68]">{(file.size / 1024).toFixed(1)} Ko</span>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1">Catégorie par défaut</label>
            <input type="text" value={defaultCategory} onChange={e => setDefaultCategory(e.target.value)} className="w-full border border-[#E0DFDD] px-3 py-2 text-sm" data-testid="default-category-input" />
            <p className="text-xs text-[#6B6A68] mt-1">Toutes les commandes seront classées ici, à ranger après.</p>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Multiplicateur prix</label>
            <input type="number" step="0.01" min="0.01" value={priceMultiplier} onChange={e => setPriceMultiplier(Number(e.target.value) || 1)} className="w-full border border-[#E0DFDD] px-3 py-2 text-sm" data-testid="price-multiplier-input" />
            <p className="text-xs text-[#6B6A68] mt-1">Ex: si prix Shopify en USD, mettre ~135 (1 USD ≈ 135 DA).</p>
          </div>
          <div className="flex items-center pt-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={skipDuplicates} onChange={e => setSkipDuplicates(e.target.checked)} className="accent-[#1A1514]" data-testid="skip-duplicates-checkbox" />
              Ignorer les produits déjà importés
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={handlePreview} disabled={!file || previewing || importing} className="px-5 py-2 border border-[#1A1514] text-sm font-medium hover:bg-[#F9F8F6] disabled:opacity-40 flex items-center gap-2" data-testid="preview-btn">
            {previewing ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            Prévisualiser
          </button>
          <button onClick={handleImport} disabled={!file || importing || previewing} className="px-5 py-2 bg-[#1A1514] text-white text-sm font-medium disabled:opacity-40 flex items-center gap-2" data-testid="import-btn">
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />}
            {importing ? 'Importation en cours...' : 'Importer maintenant'}
          </button>
        </div>
      </div>

      {/* Preview results */}
      {preview && !importResult && (
        <div className="bg-white border border-[#E0DFDD] p-6 rounded space-y-4" data-testid="preview-results">
          <h3 className="font-serif text-lg">Aperçu — {preview.stats?.total_products || 0} produits trouvés</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Stat label="Produits" value={preview.stats?.total_products || 0} />
            <Stat label="Variantes" value={preview.stats?.total_variants || 0} />
            <Stat label="Images" value={preview.stats?.total_images || 0} />
            <Stat label="Sans prix" value={preview.stats?.products_without_price || 0} alert />
          </div>

          {preview.warnings?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-xs space-y-1">
              <div className="flex items-center gap-2 font-medium text-yellow-800"><AlertCircle size={14} /> Avertissements ({preview.warnings.length})</div>
              {preview.warnings.slice(0, 5).map((w, i) => <div key={i} className="text-yellow-700">• {w}</div>)}
            </div>
          )}

          <div className="max-h-96 overflow-y-auto border border-[#E0DFDD]">
            <table className="w-full text-xs">
              <thead className="bg-[#F9F8F6] sticky top-0">
                <tr><th className="text-left p-2">Nom</th><th className="text-left p-2">Prix</th><th className="text-left p-2">Variantes</th><th className="text-left p-2">Images</th></tr>
              </thead>
              <tbody>
                {preview.products?.slice(0, 50).map((p, i) => (
                  <tr key={i} className="border-t border-[#E0DFDD]">
                    <td className="p-2">{p.name}</td>
                    <td className="p-2">{p.price > 0 ? `${p.price} DA` : <span className="text-red-500">—</span>}</td>
                    <td className="p-2">{p.variants?.length || 0}</td>
                    <td className="p-2">{p.images?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.products?.length > 50 && <div className="p-2 text-center text-[#6B6A68] text-xs">… et {preview.products.length - 50} autres</div>}
          </div>
        </div>
      )}

      {/* Import results */}
      {importResult && (
        <div className="bg-green-50 border border-green-200 p-6 rounded space-y-3" data-testid="import-results">
          <div className="flex items-center gap-2 text-green-800 font-medium"><CheckCircle2 size={18} /> Import terminé</div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Stat label="Importés" value={importResult.inserted} />
            <Stat label="Ignorés (doublons)" value={importResult.skipped} />
            <Stat label="Échoués" value={importResult.failed} alert={importResult.failed > 0} />
          </div>
          {importResult.errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 p-3 rounded text-xs space-y-1">
              <div className="font-medium text-red-800">Erreurs :</div>
              {importResult.errors.map((e, i) => <div key={i} className="text-red-700">• {e}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, alert = false }) {
  return (
    <div className={`p-3 rounded text-center ${alert && value > 0 ? 'bg-red-50 border border-red-200' : 'bg-[#F9F8F6]'}`}>
      <div className={`text-xl font-bold ${alert && value > 0 ? 'text-red-700' : 'text-[#1A1514]'}`}>{value}</div>
      <div className="text-xs text-[#6B6A68]">{label}</div>
    </div>
  );
}
